import { MusicChip, MusicAttachButton } from "@/components/platform/MusicAttach";
import { parseMusicTag, encodeMusicTag, type AttachedTrack } from "@/lib/musicTag";
import { useEffect, useRef, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Link, useParams, useSearchParams } from "react-router-dom";
import { formatDistanceToNow } from "date-fns";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Hand, Bookmark, Trash2, Send, Radio, Sparkles, MessageCircle, Users, Ticket, ImagePlus, Smile, X } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { useMediaUpload } from "@/hooks/platform/useMediaUpload";
import ClapButton from "@/components/platform/ClapButton";
import PostComments from "@/components/platform/PostComments";
import MediaCarousel from "@/components/platform/MediaCarousel";
import GetTicketsButton from "@/components/platform/GetTicketsButton";
import ShareButton from "@/components/platform/ShareButton";
import { isHiddenTag, isPlacedIn, promotionLink } from "@/lib/promoLink";
import RepostButton, { repostSourceId } from "@/components/platform/RepostButton";
import RepostEmbed from "@/components/platform/RepostEmbed";

import MentionTextarea, { extractMentions } from "@/components/platform/MentionTextarea";
import { useCommentCounts } from "@/hooks/platform/useComments";

import { toast } from "sonner";
import { useCurrentUser } from "@/hooks/platform/useCurrentUser";
import { useFeedPosts, useFollowingPosts, useMyPosts, useCreatePost, useDeletePost, useLikePost, useUserLikeStatuses, type PostRow } from "@/hooks/platform/usePosts";
import { useSavedItems, useToggleSaved } from "@/hooks/platform/useSocial";
import { mediaUrls, isVideoUrl } from "@/lib/mediaUrl";
import { useAuthors } from "@/hooks/platform/useAuthors";
import { PageShell, LoadingState, ErrorState } from "@/components/dashboard/common/PageShell";
import ArtBlock from "@/components/platform/ArtBlock";
import AuthorChip from "@/components/platform/AuthorChip";
import ShowcaseNotice, { SampleBadge } from "@/components/platform/ShowcaseNotice";
import ItemDetailDialog, { type DetailAction, type DetailItem } from "@/components/platform/ItemDetailDialog";
import { SHOWCASE_POSTS, compactNumber } from "@/lib/showcaseData";

type FeedMode = "public" | "following" | "mine";

interface FeedBoardProps {
  title?: string;
  description?: string;
  mode?: FeedMode;
  composer?: boolean;
}

const FeedBoard = ({ title = "Feed", description = "What's happening across Metro City Groove.", mode = "public", composer = true }: FeedBoardProps) => {
  const { userId } = useCurrentUser();
  const publicFeed = useFeedPosts();
  const followingFeed = useFollowingPosts(mode === "following" ? userId : null);
  const mineFeed = useMyPosts(mode === "mine" ? userId : null);
  const active = mode === "following" ? followingFeed : mode === "mine" ? mineFeed : publicFeed;

  const createPost = useCreatePost();
  const deletePost = useDeletePost();
  const likePost = useLikePost();
  const { data: saved = [] } = useSavedItems(userId, "post");
  const toggleSaved = useToggleSaved();
  const savedIds = new Set(saved.map((s) => s.item_id));
  const [draft, setDraft] = useState("");
  const [pictures, setPictures] = useState<{ file: File; preview: string }[]>([]);
  const fileInput = useRef<HTMLInputElement>(null);
  const { upload, uploading } = useMediaUpload("creator-media");
  const [posting, setPosting] = useState(false);
  const publishingRef = useRef(false);
  const [track, setTrack] = useState<AttachedTrack | null>(null);
  const publish = async () => {
    if (!userId || (!draft.trim() && !pictures.length) || posting || publishingRef.current) return;
    publishingRef.current = true;
    setPosting(true);
    try {
      const urls: string[] = [];
      for (const picture of pictures) {
        const url = await upload(picture.file);
        if (!url) throw new Error("A picture could not be uploaded. Please try again.");
        urls.push(url);
      }
      await createPost.mutateAsync({ author_id: userId, content: draft.trim(), post_type: "post", media: urls, tags: [...extractMentions(draft).map((m) => `@${m}`), ...(track ? [encodeMusicTag(track)] : [])] });
      setTrack(null);
      pictures.forEach((picture) => URL.revokeObjectURL(picture.preview));
      setPictures([]);
      setDraft("");
      toast.success("Posted to the feed");
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "Post failed");
    } finally {
      publishingRef.current = false;
      setPosting(false);
    }
  };
  const addPictures = (files: FileList | null) => {
    if (!files) return;
    const images = Array.from(files).filter((file) => file.type.startsWith("image/"));
    setPictures((current) => [...current, ...images.slice(0, 4 - current.length).map((file) => ({ file, preview: URL.createObjectURL(file) }))]);
    if (fileInput.current) fileInput.current.value = "";
  };
  const [detail, setDetail] = useState<DetailItem | null>(null);
  const [actions, setActions] = useState<DetailAction[]>([]);
  const [openComments, setOpenComments] = useState<Record<string, boolean>>({});
  const [searchParams] = useSearchParams();
  const { id: routePostId } = useParams();
  const focusedPostId = routePostId ?? searchParams.get("post");
  const { data: focusedPost, error: focusedPostError } = useQuery({
    queryKey: ["focused-feed-post", focusedPostId], enabled: !!focusedPostId,
    queryFn: async () => {
      const { data, error } = await supabase.from("posts").select("*").eq("id", focusedPostId!).eq("visibility", "public").maybeSingle();
      if (error) throw error;
      return data;
    },
  });

  useEffect(() => {
    if (focusedPostId && (focusedPost || active.data?.some((post) => post.id === focusedPostId))) {
      document.getElementById(`feed-post-${focusedPostId}`)?.scrollIntoView({ block: "center" });
    }
  }, [focusedPostId, active.data, focusedPost]);

  const feedRows = (active.data ?? []) as PostRow[];
  const posts = [...(focusedPost && !feedRows.some((post) => post.id === focusedPost.id) ? [focusedPost] : []), ...feedRows].filter((post) => routePostId ? post.id === routePostId : mode === "mine" || post.id === focusedPostId || !post.tags?.includes("promotion") || isPlacedIn(post.tags, "feed"));
  const { data: commentCounts = {} } = useCommentCounts(posts.filter((post) => !post.tags?.includes("promotion")).map((post) => post.id));
  const { getAuthor } = useAuthors([...posts.map((p) => p.author_id), userId]);
  const me = getAuthor(userId);
  
  // Fetch like status for all posts at once
  const postIds = posts.map((p) => p.id);
  const { data: likeStatuses = [] } = useUserLikeStatuses(postIds, userId);
  const likedMap = new Map(likeStatuses.map((like) => [like.post_id, true]));

  const openPost = (post: PostRow) => {
    const media = mediaUrls(post.media);
    setDetail({
      id: post.id,
      title: post.content?.slice(0, 70) || "Post",
      kindLabel: post.post_type,
      images: media,
      icon: Radio,
      author: { ...getAuthor(post.author_id), meta: formatDistanceToNow(new Date(post.created_at), { addSuffix: true }) },
      description: post.content,
      tags: post.tags ?? [],
      facts: [
        { label: "Claps", value: post.like_count ?? 0, icon: Hand },
        { label: "Type", value: post.post_type },
        { label: "Photos", value: media.length || "None" },
      ],
    });
    setActions([
      ...(!post.tags?.includes("promotion") ? [{ label: "Clap", icon: Hand, variant: "outline" as const, onClick: () => userId && likePost.mutate({ postId: post.id, userId }) }] : []),
      ...(userId ? [{ label: savedIds.has(post.id) ? "Saved" : "Save", icon: Bookmark, variant: "outline" as const, onClick: () => toggleSaved.mutate({ userId, itemType: "post", itemId: post.id, isSaved: savedIds.has(post.id) }) }] : []),
      ...(post.event_id ? [{ label: "Event tickets", icon: Ticket, variant: "outline" as const, onClick: () => window.location.assign(`/events?event=${post.event_id}`) }] : []),
    ]);
  };

  return (
    <PageShell title={title}>
      <div className="border-b border-border pb-3"><h1 className="text-xl font-semibold text-foreground">{title}</h1><p className="text-sm text-muted-foreground">{description}</p></div>

      {composer && userId && (
        <Card className="bg-gray-900/60 border-gray-800 max-w-2xl">
          <CardContent className="p-4 space-y-3">
            <AuthorChip author={me} size="md" />
            <MentionTextarea
              value={draft}
              onChange={setDraft}
              onSubmit={() => void publish()}
              placeholder="Share a drop, a gig, or a moment… use @ to mention someone"
              className="border-gray-800 min-h-[80px]"
            />
            {pictures.length > 0 && <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">{pictures.map((picture) => <div key={picture.preview} className="relative aspect-square min-w-0"><img src={picture.preview} alt="Picture for post" className="h-full w-full rounded object-cover" /><Button type="button" variant="secondary" size="icon" aria-label="Remove picture" className="absolute right-1 top-1 h-7 w-7" onClick={() => { URL.revokeObjectURL(picture.preview); setPictures((current) => current.filter((item) => item !== picture)); }}><X className="h-4 w-4" /></Button></div>)}</div>}
            <div className="flex items-center gap-1">
              <input ref={fileInput} type="file" accept="image/*" multiple hidden onChange={(event) => addPictures(event.target.files)} />
              <Button type="button" variant="ghost" size="icon" title="Add pictures" aria-label="Add pictures" disabled={pictures.length >= 4 || posting} onClick={() => fileInput.current?.click()}><ImagePlus className="h-5 w-5" /></Button>
              <Popover><PopoverTrigger asChild><Button type="button" variant="ghost" size="icon" title="Add emoji" aria-label="Add emoji"><Smile className="h-5 w-5" /></Button></PopoverTrigger><PopoverContent align="start" className="w-64 p-2"><div className="grid grid-cols-6 gap-1">{["😀", "😂", "😍", "🙌", "👏", "🔥", "🎶", "🎧", "🎤", "💃", "❤️", "✨", "🎉", "🥳", "😎", "💯", "📍", "🎟️"].map((emoji) => <Button key={emoji} type="button" variant="ghost" size="icon" aria-label={`Add ${emoji}`} onClick={() => setDraft((value) => value + emoji)}>{emoji}</Button>)}</div></PopoverContent></Popover>
              <MusicAttachButton value={track} onChange={setTrack} />
              <span className="ml-auto" />
              <Button
                size="sm"
                disabled={(!draft.trim() && !pictures.length) || posting || uploading}
                onClick={() => void publish()}
              >
                <Send className="w-4 h-4 mr-2" />{posting ? "Posting…" : "Post"}
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {routePostId && focusedPostError ? (
        <ErrorState message="Could not load this post." />
      ) : routePostId && focusedPost === undefined ? (
        <LoadingState label="Loading post…" />
      ) : routePostId && !focusedPost ? (
        <ErrorState message="This post is unavailable." />
      ) : active.isLoading && !routePostId ? (
        <LoadingState label="Loading the feed…" />
      ) : active.error && !routePostId ? (
        <ErrorState message={(active.error as Error).message} onRetry={() => active.refetch()} />
      ) : posts.length === 0 ? (
        <div className="space-y-4 max-w-2xl">
          <ShowcaseNotice label={mode === "following" ? "Following preview" : "Feed preview"} />
          {SHOWCASE_POSTS.map((p) => (
            <Card
              key={p.id}
              role="button"
              tabIndex={0}
              onClick={() => {
                setDetail({
                  id: p.id,
                  title: p.content.slice(0, 70),
                  kindLabel: p.kind,
                  hue: p.author.hue,
                  icon: Radio,
                  author: { id: p.author.id, name: p.author.name, handle: p.author.handle, verified: p.author.verified, meta: `${p.hoursAgo}h ago` },
                  description: p.content,
                  tags: p.tags,
                  sample: true,
                  facts: [
                    { label: "Claps", value: compactNumber(p.likes), icon: Hand },
                    { label: "City", value: p.author.city },
                    { label: "Followers", value: compactNumber(p.author.followers), icon: Users },
                    { label: "Rating", value: p.author.rating },
                  ],
                  relatedTitle: "More from this artist",
                  related: SHOWCASE_POSTS.filter((o) => o.author.id === p.author.id && o.id !== p.id).slice(0, 4).map((o) => ({ id: o.id, title: o.content.slice(0, 30), meta: `${compactNumber(o.likes)} likes`, hue: o.author.hue })),
                });
                setActions([]);
              }}
              className="bg-gray-900/60 border-gray-800 cursor-pointer hover:border-purple-600/50 transition-colors"
            >
              <CardContent className="p-4 space-y-3">
                <div className="flex items-start gap-3">
                  <AuthorChip author={{ id: p.author.id, name: p.author.name, handle: p.author.handle, verified: p.author.verified, meta: `${p.hoursAgo}h ago` }} />
                  <SampleBadge />
                </div>
                <p className="text-sm text-gray-200">{p.content}</p>
                {p.hasMedia && <ArtBlock seed={p.id} icon={Radio} hue={p.author.hue} className="h-56 w-full rounded-xl" label={p.kind} />}
                <div className="flex flex-wrap gap-1">
                  {p.tags.map((t) => <Badge key={t} variant="outline" className="border-gray-700 text-gray-500 text-[10px]">#{t}</Badge>)}
                </div>
                <p className="flex items-center gap-1.5 text-xs text-gray-400"><Hand className="w-4 h-4 text-pink-400" />{compactNumber(p.likes)}</p>
              </CardContent>
            </Card>
          ))}
        </div>
      ) : (
        <div className="space-y-4 max-w-2xl">
          {posts.map((post) => {
            const media = mediaUrls(post.media);
            return (
              <Card id={`feed-post-${post.id}`} key={post.id} className={`bg-card border-border hover:border-primary/40 transition-colors ${focusedPostId === post.id ? "ring-1 ring-primary" : ""}`}>
                <CardContent className="p-4 space-y-3">
                  <div className="flex items-start gap-3">
                    <AuthorChip
                      author={{ ...getAuthor(post.author_id), meta: formatDistanceToNow(new Date(post.created_at), { addSuffix: true }) }}
                    />
                    <Badge variant="outline" className="border-gray-700 text-gray-400 capitalize shrink-0">{post.post_type}</Badge>
                    {post.tags?.includes("promotion") && <Badge variant="outline">Promotion</Badge>}
                  </div>
                  {post.content && (
                    <Button type="button" variant="ghost" className="block h-auto w-full whitespace-normal p-0 text-left hover:bg-transparent" onClick={() => openPost(post)}>
                      <p className="text-sm text-gray-200 whitespace-pre-wrap">{post.content}</p>
                    </Button>
                  )}
                  {parseMusicTag(post.tags) && <MusicChip track={parseMusicTag(post.tags)!} />}
                  {repostSourceId(post.tags) && <RepostEmbed sourceId={repostSourceId(post.tags) ?? ""} />}
                  {media.length > 0 && (
                    <MediaCarousel media={media} onItemClick={() => openPost(post)} />
                  )}
                  {(post.event_id || promotionLink(post.tags)) && <div className="flex flex-wrap gap-2">
                    {post.event_id && <GetTicketsButton eventId={post.event_id} variant={post.tags?.includes("promotion") ? "default" : "outline"} />}
                    {promotionLink(post.tags) && <Button asChild size="sm" variant="outline"><a href={promotionLink(post.tags)!} target="_blank" rel="noopener noreferrer nofollow">Visit link</a></Button>}
                  </div>}

                  {(post.tags ?? []).length > 0 && (
                    <div className="flex flex-wrap gap-1">
                      {(post.tags ?? []).filter((t) => !isHiddenTag(t)).map((t) => <Badge key={t} variant="outline" className="border-gray-700 text-gray-500 text-[10px]">#{t}</Badge>)}
                    </div>
                  )}
                  <div className="flex flex-wrap items-center gap-1 pt-1">
                    {!post.tags?.includes("promotion") && <>
                    <ClapButton 
                      count={post.like_count} 
                      liked={likedMap.get(post.id)} 
                      onClap={() => userId && likePost.mutate({ postId: post.id, userId })} 
                    />

                    <Button type="button" variant="ghost" size="sm"
                      className="gap-1.5 text-xs text-muted-foreground"
                      onClick={() => setOpenComments((s) => ({ ...s, [post.id]: !s[post.id] }))}
                      aria-expanded={!!openComments[post.id]}
                    >
                      <MessageCircle className="w-4 h-4" />
                      {commentCounts[post.id] ? `${commentCounts[post.id]} ` : ""}Comment
                    </Button>
                    </>}

                    {userId && (
                      <Button type="button" variant="ghost" size="sm" className="gap-1.5 text-xs text-muted-foreground" onClick={() => toggleSaved.mutate({ userId, itemType: "post", itemId: post.id, isSaved: savedIds.has(post.id) })}>
                        <Bookmark className={`w-4 h-4 ${savedIds.has(post.id) ? "fill-purple-400 text-purple-400" : ""}`} />Save
                      </Button>
                    )}
                    <RepostButton postId={post.id} className="text-xs text-muted-foreground" />
                    <ShareButton path={`/post/${post.id}`} title={post.content?.slice(0, 70) || "Post on Metro City Groove"} previewImage={media[0]} variant="ghost" />
                    {userId === post.author_id && (
                      <Button type="button" variant="ghost" size="sm" className="gap-1.5 text-xs text-muted-foreground ml-auto" onClick={() => deletePost.mutate(post.id, { onSuccess: () => toast.success("Post deleted") })}>
                        <Trash2 className="w-4 h-4" />Delete
                      </Button>
                    )}
                  </div>

                  {openComments[post.id] && <PostComments postId={post.id} />}
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}
      <ItemDetailDialog item={detail} actions={actions} onClose={() => setDetail(null)} />
    </PageShell>
  );
};

export default FeedBoard;
